import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import UploadIcon from "@mui/icons-material/Upload";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useTranslation } from "react-i18next";
import httpClient from "api/httpClient";
import ConfirmationDialog from "components/Common/ConfirmationDialog";
import { CustomTablePagination } from "components/Common/CustomTablePagination";
import { CustomMenuButton } from "components/Common/CustomMenuButton";
import ImportExcelDialog from "components/Common/ImportExcelDialog";
import SchemaDialogForm from "components/Form/SchemaDialogForm";
import InsureesTableData from "./data/InsureesTableData";
import { insureesFormSchema, insureesRecord } from "./data/InsureesSchemas";
import { handleApiError } from "utils/apiError";

function Insurees() {
  const apiBase = process.env.REACT_APP_API_URL;
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(insureesRecord);
  const [dialogTitle, setDialogTitle] = useState("Create Insuree");
  const [saving, setSaving] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [policiesLoading, setPoliciesLoading] = useState(false);
  const [policyTypes, setPolicyTypes] = useState([]);
  const [policyTypesLoading, setPolicyTypesLoading] = useState(false);
  const [sb, setSb] = useState({ open: false, color: "error", title: "", message: "" });
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [confirmationDialogMessage, setConfirmationDialogMessage] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchInput, setSearchInput] = useState("");

  const showSnackBar = (title, type, message) => {
    setSb({ open: true, title, color: type === "error" ? "error" : "success", message });
  };

  const loadPolicies = useCallback(
    async (search = "") => {
      try {
        setPoliciesLoading(true);
        const url = `${apiBase}/api/policies/search-list?page=0&size=50&q=${search}`;
        const res = await httpClient.get(url);
        const data = res.data.content || [];
        setPolicies(data.map((p) => ({ ...p, label: `${p.code} (${p.employerName})` })));
      } catch (err) {
        console.error("Failed to load policies", err);
      } finally {
        setPoliciesLoading(false);
      }
    },
    [apiBase]
  );

  const loadPolicyTypes = useCallback(
    async (search = "") => {
      try {
        setPolicyTypesLoading(true);
        const url = `${apiBase}/api/policy-types/search-list?page=0&size=50&q=${search}`;
        const res = await httpClient.get(url);
        const data = res.data.content || [];
        setPolicyTypes(data.map((t) => ({ ...t, label: t.name })));
      } catch (err) {
        console.error("Failed to load policy types", err);
      } finally {
        setPolicyTypesLoading(false);
      }
    },
    [apiBase]
  );

  const loadInsurees = useCallback(
    async (q = "") => {
      try {
        setLoading(true);
        const url = q
          ? `${apiBase}/api/insurees/search-list?page=${page}&size=${rowsPerPage}&q=${q}`
          : `${apiBase}/api/insurees?page=${page}&size=${rowsPerPage}`;
        const res = await httpClient.get(url);
        setItems(res.data.content || []);
        setTotalElements(res.data.totalElements || 0);
      } catch (err) {
        handleApiError(err, { showSnackBar });
      } finally {
        setLoading(false);
      }
    },
    [apiBase, page, rowsPerPage]
  );

  useEffect(() => {
    loadInsurees(searchInput);
  }, [loadInsurees]);

  useEffect(() => {
    loadPolicies();
    loadPolicyTypes();
  }, [loadPolicies, loadPolicyTypes]);

  // Restored missing function
  const openCreateDialog = () => {
    setEditingRecord(insureesRecord);
    setDialogTitle("Create Insuree");
    setDialogOpen(true);
  };

  const openEditDialog = (rec) => {
    setEditingRecord({ ...rec });
    setDialogTitle("Edit Insuree");
    setDialogOpen(true);
  };

  const onSubmit = async (data) => {
    console.log("Submitting Policy Data:", data);
    setSaving(true);
    try {
      const isEdit = data.id && Number(data.id) > 0;
      const url = isEdit ? `${apiBase}/api/insurees/${data.id}` : `${apiBase}/api/insurees`;
      await httpClient[isEdit ? "put" : "post"](url, data);
      showSnackBar("Success", "success", `Insuree ${isEdit ? "updated" : "created"}`);
      setDialogOpen(false);
      loadInsurees(searchInput);
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setSaving(false);
    }
  };

  const onDeleteConfirmed = async () => {
    try {
      if (Array.isArray(itemToDelete)) {
        await httpClient.post(`${apiBase}/api/insurees/bulk-delete`, { ids: itemToDelete });
      } else {
        await httpClient.delete(`${apiBase}/api/insurees/${itemToDelete.id}`);
      }
      showSnackBar("Success", "success", "Item(s) deleted successfully");
      loadInsurees(searchInput);
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setConfirmationDialogOpen(false);
      setItemToDelete(null);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Card>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
            <MDBox display="flex" gap={1}>
              <MDButton variant="gradient" color="info" size="small" onClick={openCreateDialog}>
                Create Insuree <Icon>add</Icon>
              </MDButton>
              <MDButton
                variant="gradient"
                color="dark"
                size="small"
                onClick={() => setImportOpen(true)}
              >
                Import <UploadIcon fontSize="small" />
              </MDButton>
            </MDBox>
            <MDBox display="flex" gap={1}>
              <MDInput
                size="small"
                label="Search Insurees"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyUp={(e) => e.key === "Enter" && loadInsurees(searchInput)}
              />
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                onClick={() => loadInsurees(searchInput)}
              >
                {t("search.go")}
              </MDButton>
              <CustomMenuButton
                buttonTitle="Actions"
                items={[
                  { key: "all", text: "Select All", icon: "select_all" },
                  { key: "none", text: "Unselect All", icon: "deselect" },
                  { key: "del", text: "Delete", icon: "delete", iconColor: "error" },
                ]}
                onItemClicked={(item) => {
                  if (item.key === "del") {
                    setItemToDelete(selectedIds);
                    setConfirmationDialogMessage("Delete selected?");
                    setConfirmationDialogOpen(true);
                  } else if (item.key === "all") {
                    setSelectedIds(items.map((i) => i.id));
                  } else {
                    setSelectedIds([]);
                  }
                }}
              />
            </MDBox>
          </MDBox>
          <MDBox px={3} pb={3}>
            {loading ? (
              <MDTypography variant="button">Loading...</MDTypography>
            ) : items.length === 0 ? (
              <MDTypography variant="button">No insurees found.</MDTypography>
            ) : (
              <>
                <InsureesTableData
                  data={items}
                  selectedIds={selectedIds}
                  onToggle={(id) =>
                    setSelectedIds((prev) =>
                      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                    )
                  }
                  onEdit={openEditDialog}
                  onDelete={(rec) => {
                    setItemToDelete(rec);
                    setConfirmationDialogMessage(`Delete ${rec.name}?`);
                    setConfirmationDialogOpen(true);
                  }}
                />
                <CustomTablePagination
                  page={page}
                  rowsPerPage={rowsPerPage}
                  totalItems={totalElements}
                  onPageChange={setPage}
                  onRowsPerPageChange={(n) => {
                    setRowsPerPage(n);
                    setPage(0);
                  }}
                />
              </>
            )}
          </MDBox>
        </Card>
      </MDBox>

      <ImportExcelDialog
        open={importOpen}
        title="Import Insurees"
        endpoint={`${apiBase}/api/insurees/import`}
        templateEndpoint={`${apiBase}/api/insurees/template`}
        onClose={() => setImportOpen(false)}
        onDone={() => loadInsurees("")}
        showSnackBar={showSnackBar}
      />

      <SchemaDialogForm
        open={dialogOpen}
        title={dialogTitle}
        schema={insureesFormSchema}
        initialRecord={editingRecord}
        saving={saving}
        onClose={() => setDialogOpen(false)}
        onSubmit={onSubmit}
        deps={{
          policies: policies,
          policiesLoading: policiesLoading,
          onSearchPolicies: loadPolicies,
          policyTypes: policyTypes,
          policyTypesLoading: policyTypesLoading,
          onSearchPolicyTypes: loadPolicyTypes,
        }}
      />
      <ConfirmationDialog
        open={confirmationDialogOpen}
        title="Confirm Deletion"
        message={confirmationDialogMessage}
        onCancel={() => setConfirmationDialogOpen(false)}
        onOk={onDeleteConfirmed}
      />
      <MDSnackbar
        color={sb.color}
        icon={sb.color === "error" ? "warning" : "check_circle"}
        title={sb.title}
        content={sb.message}
        open={sb.open}
        onClose={() => setSb({ ...sb, open: false })}
      />
    </DashboardLayout>
  );
}

export default Insurees;
